import { useRef, useEffect, useReducer, useCallback } from 'react';

// hook that runs callback only on update, not on initial mount
var useUpdateEffect = function (callback, deps) {
    var isInitialMount = useRef(true);
    useEffect(function () {
        if (isInitialMount.current) {
            isInitialMount.current = false;
            return;
        }
        callback();
    }, deps); // eslint-disable-line react-hooks/exhaustive-deps
};
var areCallbacksEqual = function (cb1, cb2) {
    return cb1.id === cb2.id;
};
var useRerender = function () { return useReducer(function (s) { return s + 1; }, 0)[1]; };
// NOTE: callback collection is implemented via ref (instead of state) because of the use cases where we
// need to have updated collection immediately instead of waiting for the next rendering cycle
var useCallbackManager = function () {
    var callbacks = useRef([]);
    var rerender = useRerender();
    var register = function (cb) {
        var id = cb.id, fn = cb.fn;
        callbacks.current.push({ id: id, fn: fn });
        rerender();
    };
    var update = function (cb) {
        var id = cb.id, fn = cb.fn;
        var updateIndex = callbacks.current.findIndex(function (item) { return areCallbacksEqual(item, { id: id, fn: fn }); });
        if (updateIndex !== -1) {
            callbacks.current[updateIndex].fn = fn;
            rerender();
        }
    };
    var remove = function (cb) {
        var id = cb.id, fn = cb.fn;
        var removeIndex = callbacks.current.findIndex(function (item) { return areCallbacksEqual(item, { id: id, fn: fn }); });
        if (removeIndex !== -1) {
            callbacks.current.splice(removeIndex, 1);
            rerender();
        }
    };
    useEffect(function () {
        return function () {
            callbacks.current = [];
        };
    }, []);
    return {
        callbacks: callbacks.current,
        register: register,
        update: update,
        remove: remove,
    };
};

var createEffect = function (_a) {
    var register = _a.register, update = _a.update, remove = _a.remove;
    return function (fn, deps, debuggingId) {
        if (deps === void 0) { deps = []; }
        var id = useRef();
        var callbackFn = useCallback(fn, deps);
        useUpdateEffect(function () {
            if (id.current) {
                update({ id: id.current, fn: callbackFn });
            }
        }, [callbackFn]);
        useEffect(function () {
            id.current = Symbol(debuggingId);
            register({ id: id.current, fn: callbackFn });
            return function () {
                if (id.current) {
                    remove({ id: id.current, fn: callbackFn });
                }
            };
        }, []); // eslint-disable-line react-hooks/exhaustive-deps
    };
};
// useCustomEffect hook enables possibility to create effect which is triggered by some runtime event;
// useCustomEffect API extracts array with two output items:
// - [0]: useCustomEffectHook - custom effect hook that accepts 3 arguments (checkout createEffect function which creates custom effect hook):
//    * fn - callback function (like subscriber) which runs every time when specified runtime event triggers;
//    * deps - dependency array where you have to put all dynamic params used in callback function (otherwise you callback won't use fresh params)
//    * debuggingId - symbol identifier is used for each callback function, so if you want to specify readable id for debugging purposes, you can
//      do that by defining debuggingId;
// useCustomEffectHook can be used multiple times in a different components on a different levels;
// consumer just need to have an access to the useCustomEffectHook to be able to subscribe to the custom effect
// - [1]: run - function that specifies the effect trigger;
// run function can accept parameters which should be passed to the custom effect hook callback.
// useCustomEffect is powered by standard observer pattern implementation
// (effect event acts as observable and callbacks passed to custom effect hook act as observers)
// observers management (subscribe, callback updates & unsubscribe) is handled under the hood by including consumer components lifecycle
// (checkout createEffect function implementation)
var useCustomEffect = function () {
    // useCallbackManager - callbacks/observers CRUD service
    var _a = useCallbackManager(), callbacks = _a.callbacks, register = _a.register, update = _a.update, remove = _a.remove;
    var useCustomEffectHook = createEffect({
        register: register,
        update: update,
        remove: remove,
    });
    var runEffect = useCallback(function () {
        var params = [];
        for (var _i = 0; _i < arguments.length; _i++) {
            params[_i] = arguments[_i];
        }
        callbacks.forEach(function (callback) {
            callback.fn.apply(callback, params);
        });
    }, [callbacks]);
    var run = useCallback(function () {
        var params = [];
        for (var _i = 0; _i < arguments.length; _i++) {
            params[_i] = arguments[_i];
        }
        runEffect.apply(void 0, params);
    }, [runEffect]);
    return [useCustomEffectHook, run];
};

export { useCustomEffect };
//# sourceMappingURL=index.es.js.map
